import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { extractErrorMessage } from '@shared/services/blockingError';
import { personService } from '@shared/services/personService';

function extractResponseMessage(response) {
    if (!response) {
        return null;
    }

    if (typeof response === 'string') {
        return response;
    }

    return response.message || response.error || response.detail || null;
}

export const useFamilyForm = (person) => {
    const { t } = useTranslation();
    const [familyMembers, setFamilyMembers] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const debounceTimeout = useRef(null);

    const currentYear = new Date().getFullYear();
    
    const validationSchema = yup.object().shape({
        citizenship: yup.string().required(t('validation.person.family.citizenshipRequired')),
        firstName: yup.string()
            .required(t('validation.person.family.nameRequired'))
            .max(30, t('validation.person.family.nameLength')),
        middleName: yup.string(),
        lastName: yup.string(),
        name: yup.string(),  // This will be auto-populated
        idn: yup.string()
            .required(t('validation.person.family.idnRequired'))
            .matches(/^\d{10}$/, t('persons.form.idn-10')),
        birthDate: yup.mixed()
            .test('is-required', t('validation.person.family.birthDateRequired'), function(value) {
                return value !== '' && value !== null && value !== undefined;
            })
            .nullable(),
        school: yup.boolean(),
        childOrder: yup.string()
            .nullable()
            .transform((value) => (value === '' ? null : value))
            .when('school', {
                is: true,
                then: () => yup.string().required(t('validation.person.family.childOrderRequired'))
            }),
        hasTaxRelief: yup.boolean(),
        taxReliefYear: yup.number()
            .nullable()
            .transform((value) => (value === '' ? null : value))
            .when('hasTaxRelief', {
                is: true,
                then: () => yup.number().required(t('validation.person.family.taxReliefYearRequired'))
                    .min(currentYear - 4).max(currentYear)
            }),
        taxReliefType: yup.string()
            .nullable()
            .when('hasTaxRelief', {
                is: true,
                then: () => yup.string().required(t('validation.person.family.taxReliefTypeRequired'))
            }),
        taxReliefPeriod: yup.string()
            .nullable()
            .when('hasTaxRelief', {
                is: true,
                then: () => yup.string().required(t('validation.person.family.taxReliefPeriodRequired'))
            })
    });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            citizenship: 'bulgarian',
            firstName: '',
            middleName: '',
            lastName: '',
            name: '',
            idn: '',
            birthDate: null,
            school: false,
            childOrder: '',
            hasTaxRelief: false,
            taxReliefYear: currentYear,
            taxReliefType: '',
            taxReliefPeriod: ''
        }
    });

    const hasTaxRelief = watch('hasTaxRelief');

    const transformFamilyMember = useCallback((member) => {
        if (!member) {
            return null;
        }

        const childOrderValue = member.childNum !== undefined && member.childNum !== null
            ? String(member.childNum)
            : '';

        return {
            id: member.id ?? null,
            name: member.name ?? '',
            idn: member.govId?.idn ?? '',
            birthDate: member.dateBorn ?? null,
            citizenship: member.bgCitizenship ? 'bulgarian' : 'foreign',
            school: member.school ?? false,
            childOrder: childOrderValue,
            hasTaxRelief: Boolean(member.allowance),
            taxReliefYear: member.taxReducation ?? currentYear,
            taxReliefType: member.taxReliefType ?? '',
            taxReliefPeriod: member.taxReliefPeriod ?? ''
        };
    }, [currentYear]);

    const taxReliefTypes = [
        { value: 'article22c', label: t('persons.family.taxReliefType22c') },
        { value: 'article22d', label: t('persons.family.taxReliefType22d') }
    ];

    const taxReliefPeriods = [
        { value: 'monthly', label: t('persons.family.periodMonthly') },
        { value: 'quarterly', label: t('persons.family.periodQuarterly') },
        { value: 'annually', label: t('persons.family.periodAnnually') }
    ];

    const yearOptions = Array.from({ length: 5 }, (_, index) => ({
        value: currentYear - index,
        label: (currentYear - index).toString()
    }));

    // Enhanced IDN validation and date extraction
    const validateAndExtractFromIDN = (idn) => {
        if (idn.length !== 10) {
            setError('idn', {
                type: 'manual',
                message: t('persons.form.idn-10')
            });
            return null;
        }

        const year = parseInt(idn.substring(0, 2), 10);
        let month = parseInt(idn.substring(2, 4), 10);
        const day = parseInt(idn.substring(4, 6), 10);

        let fullYear;
        if (month > 40) {
            fullYear = 2000 + year;
            month -= 40;
        } else if (month > 20) {
            fullYear = 1800 + year;
            month -= 20;
        } else {
            fullYear = 1900 + year;
        }

        // Validate month and day
        if (month < 1 || month > 12) {
            setError('idn', {
                type: 'manual',
                message: t('persons.form.idn-invalid')
            });
            return null;
        }

        // Validate days in month
        const daysInMonth = new Date(fullYear, month, 0).getDate();
        if (day < 1 || day > daysInMonth) {
            setError('idn', {
                type: 'manual',
                message: t('persons.form.idn-invalid')
            });
            return null;
        }

        // Format date
        const dateStr = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        
        // Additional validation for future dates
        const birthDate = new Date(dateStr);
        const today = new Date();
        if (birthDate > today) {
            setError('idn', {
                type: 'manual',
                message: t('persons.form.future-date')
            });
            return null;
        }

        clearErrors('idn');
        return dateStr;
    };

    // Handle IDN changes with debounce
    const handleIdnChange = (e) => {
        const idn = e.target.value;

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (idn.length === 10) {
                const birthDate = validateAndExtractFromIDN(idn);
                if (birthDate) {
                    setValue('birthDate', birthDate);
                }
            } else if (idn.length > 0) {
                setError('idn', {
                    type: 'manual',
                    message: t('persons.form.idn-10')
                });
            }
        }, 500);
    };

    // Clean up debounce timeout
    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    // Remove the previous useEffect for IDN watching
    // and replace with custom register for IDN field
    const idnRegister = {
        ...register('idn'),
        onChange: (e) => {
            register('idn').onChange(e); // Keep the default onChange
            handleIdnChange(e); // Add our custom handler
        }
    };

    // Fetch family members on component mount
    useEffect(() => {
        const fetchFamilyMembers = async () => {
            try {
                if (person?.id) {
                    const data = await personService.getPersonFamily(person.id);
                    const transformedData = data
                        .map(transformFamilyMember)
                        .filter(Boolean);
                    setFamilyMembers(transformedData);
                }
            } catch (error) {
                console.error('Fetch error:', error);
                notifyError(extractErrorMessage(error));
            }
        };

        fetchFamilyMembers();
    }, [person?.id, t, transformFamilyMember]);

    const onSubmit = async (data) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.family.personIdRequired'));
                return false;
            }

            // Add validation check before submitting
            const birthDate = validateAndExtractFromIDN(data.idn);
            if (!birthDate) {
                notifyError(t('persons.form.idn-invalid'));
                return false;
            }

            // Combine the names
            const combinedName = `${data.firstName} ${data.middleName} ${data.lastName}`.trim();

            if (isEditing) {
                const response = await personService.updateFamilyMember(editingMember.id, { 
                    ...data,
                    personId: person.id, 
                    birthDate,
                    name: combinedName
                });

                notifySuccess(extractResponseMessage(response) || t('common.updatedSuccessfully'));
            } else {
                const payload = {
                    ...data,
                    personId: person.id,
                    childOrder: data.childOrder || null,
                    birthDate,
                    name: combinedName
                };
                const response = await personService.addFamilyMember(payload);
                notifySuccess(extractResponseMessage(response) || t('common.createdSuccessfully'));
            }

            // Refresh the family members list
            const updatedData = await personService.getPersonFamily(person.id);
            const transformedData = updatedData
                .map(transformFamilyMember)
                .filter(Boolean);
            setFamilyMembers(transformedData);
            
            handleCancel();
            return true;
        } catch (error) {
            console.error('Family member operation failed:', error);
            notifyError(extractErrorMessage(error));
            return false;
        }
    };

    const handleEdit = async (member) => {
        try {
            setIsEditing(true);
            const fetchedMember = await personService.getFamilyMemberById(member.id);
            setEditingMember(fetchedMember);

            const transformedMember = transformFamilyMember(fetchedMember);
            if (!transformedMember) {
                notifyError(t('persons.family.fetchError'));
                return;
            }

            const rawName = (transformedMember.name || '').trim();
            const nameParts = rawName ? rawName.split(/\s+/) : [];
            const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
            const lastName = nameParts.length >= 2 ? nameParts[nameParts.length - 1] : '';

            const formValues = {
                citizenship: transformedMember.citizenship,
                firstName: nameParts[0] || '',
                middleName,
                lastName,
                name: rawName,
                idn: transformedMember.idn,
                birthDate: transformedMember.birthDate,
                school: transformedMember.school,
                childOrder: transformedMember.childOrder,
                hasTaxRelief: transformedMember.hasTaxRelief,
                taxReliefYear: transformedMember.taxReliefYear,
                taxReliefType: transformedMember.taxReliefType,
                taxReliefPeriod: transformedMember.taxReliefPeriod
            };

            reset(formValues);
        } catch (error) {
            console.error('Error fetching family member details:', error);
            notifyError(extractErrorMessage(error));
            setIsEditing(false);
            setEditingMember(null);
        }
    };

    const handleDelete = async (id) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.family.personIdRequired'));
                return;
            }

            const response = await personService.deleteFamilyMember(id);
            setFamilyMembers(prev => prev.filter(member => member.id !== id));
            notifySuccess(extractResponseMessage(response) || t('common.deletedSuccessfully'));
        } catch (error) {
            notifyError(extractErrorMessage(error));
        }
    };

    const handleCancel = () => {
        reset({
            citizenship: 'bulgarian',
            firstName: '',
            middleName: '',
            lastName: '',
            name: '',
            idn: '',
            birthDate: null,
            school: false,
            childOrder: '',
            hasTaxRelief: false,
            taxReliefYear: currentYear,
            taxReliefType: '',
            taxReliefPeriod: ''
        });
        setIsEditing(false);
        setEditingMember(null);
    };

    return {
        register,
        idnRegister,
        errors,
        handleSubmit,
        onSubmit,
        watch,
        setValue,
        clearErrors,
        isEditing,
        familyMembers,
        handleEdit,
        handleDelete,
        handleCancel,
        hasTaxRelief,
        taxReliefTypes,
        taxReliefPeriods,
        yearOptions
    };
}; 